import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Clock, AlertTriangle, CheckCircle, X, Plus, FileText, Eye } from 'lucide-react';
import { colors, API, fmt, authHeaders, getTitularId, temOperacao } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
} from '../components/UI';

const TIPO_LABELS = {
  FATURA:         'Fatura',
  FATURA_CRISE:   'Fatura Crise',
  ACORDO_TITULAR: 'Acordo c/ Titular',
  ACORDO_BANCO:   'Acordo c/ Banco',
  REMUNERACAO:    'Remuneração investidor',
};
const TIPO_CORES = {
  FATURA:         colors.primary,
  FATURA_CRISE:   '#f97316',
  ACORDO_TITULAR: '#f97316',
  ACORDO_BANCO:   '#8b5cf6',
  REMUNERACAO:    '#10b981',
};
const STATUS_CORES = {
  PENDENTE:  colors.warning,
  ATRASADO:  colors.danger,
  PAGO:      colors.success,
  PARCIAL:   '#3b82f6',
  CANCELADO: colors.textDim,
};

function StatCard({ label, value, subValue, color, icon: Icon }) {
  return (
    <div style={{
      background: colors.card, borderRadius: 12, padding: 16,
      border: `1px solid ${colors.border}`, flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: color || colors.text }}>{value}</div>
          {subValue && <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>{subValue}</div>}
        </div>
        {Icon && <Icon size={20} color={color || colors.textDim} />}
      </div>
    </div>
  );
}

export default function ContasAPagarPage({ onNavigate }) {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [data, setData] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [titulares, setTitulares] = useState([]);
  // Investidor (Titular): trava o filtro no titular dele e esconde o seletor.
  // Gestor → null (vê todos). O backend filtra por titular_id quando enviado.
  // Com OPERAÇÃO o seletor abre para os titulares dela; sem operação segue
  // travado. readOnly continua valendo pra qualquer investidor — enxergar o
  // grupo não é o mesmo que poder mexer nele.
  const titularProprio = getTitularId();
  const titularRestrito = temOperacao() ? null : titularProprio;
  const readOnly = !!titularProprio; // investidor é só leitura (sem botões de ação)
  const [filtros, setFiltros] = useState({ tipo: '', status: '', mes: mesAtual, search: '', titular_id: titularRestrito ? String(titularRestrito) : '' });

  // Modal pagar
  const [pagarModal, setPagarModal] = useState(null);
  const [pagarForm, setPagarForm] = useState({ data_pagamento: '', valor_pago: '', forma_pagamento: '', observacoes: '' });
  const [saving, setSaving] = useState(false);
  const [estornandoId, setEstornandoId] = useState(null);
  const [editandoPag, setEditandoPag] = useState(null);

  // Modal detalhe (lançamentos)
  const [detalheModal, setDetalheModal] = useState(null); // item da conta
  const [baixas, setBaixas] = useState(null);        // pagamentos que não liberaram o comprometido
  const [reenviando, setReenviando] = useState(false);
  const [lancamentos, setLancamentos]   = useState([]);
  const [loadingLanc, setLoadingLanc]   = useState(false);

  const abrirDetalhe = async (item) => {
    setDetalheModal(item);
    if (!item.cartao_id || !item.mes_referencia) { setLancamentos([]); return; }
    setLoadingLanc(true);
    try {
      const r = await fetch(`${API}/transacoes?cartao_id=${item.cartao_id}&mes=${item.mes_referencia}&limit=200`);
      const d = await r.json();
      setLancamentos(Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []);
    } catch { setLancamentos([]); }
    setLoadingLanc(false);
  };

  // window.open não passa pelo interceptador de fetch, então o header
  // Authorization não vai — abria e voltava {"error":"Token não fornecido"}.
  // O token viaja na querystring, como nos links de PDF do Painel do Investidor.
  const abrirPdf = (item, e) => {
    e?.stopPropagation();
    if (!item.cartao_id || !item.mes_referencia) return;
    const tk = localStorage.getItem('xm_token') || '';
    window.open(
      `${API}/faturas/pdf/${item.cartao_id}/${item.mes_referencia}?token=${encodeURIComponent(tk)}`,
      '_blank');
  };

  const irConciliar = (item, e) => {
    e?.stopPropagation();
    if (!item.cartao_id) return;
    localStorage.setItem('conciliacao_prefiltro', JSON.stringify({
      cartao_id: item.cartao_id,
      titular_id: item.titular_id,
      mes: item.mes_referencia,
    }));
    if (onNavigate) onNavigate('conciliacao');
  };

  // Fatura está "liberada para pagar" quando:
  // - não é FATURA/FATURA_CRISE (acordos sempre podem pagar), OU
  // - é FATURA/FATURA_CRISE mas já tem conciliação OK (conferencia_ok = true ou total_lancamentos > 0)
  const podeoPagar = (item) => {
    if (item.tipo !== 'FATURA' && item.tipo !== 'FATURA_CRISE') return true;
    return !!(item.conferencia_ok || item.total_lancamentos > 0);
  };

  // Modal novo (manual)
  const [novoModal, setNovoModal] = useState(false);
  const [novoForm, setNovoForm] = useState({
    tipo: 'FATURA', cartao_id: '', titular_id: '', nome_cartao: '',
    descricao: '', valor: '', data_vencimento: '', banco: '', observacoes: '',
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
    // FATURA_CRISE NÃO entra em Contas a Pagar — vai pra Gestão de Crise > Pagamentos.
    // (não passar incluir_crise; o backend já filtra por padrão)
    try {
      // KPIs respeitam todos os filtros EXCETO status (cada card já tem seu próprio status)
      const kpisParams = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => { if (v && k !== 'status') kpisParams.set(k, v); });
      const [dataRes, kpisRes] = await Promise.all([
        fetch(`${API}/contas-a-pagar?${params}`).then(r => r.json()),
        fetch(`${API}/contas-a-pagar/kpis?${kpisParams}`).then(r => r.json()),
      ]);
      setData(dataRes.data || []);
      setKpis(kpisRes);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // com_cartao_ativo: só titular com cartão ativo entra no seletor.
    fetch(`${API}/titulares?com_cartao_ativo=true`, { headers: authHeaders() }).then(r => r.json()).then(d => {
      const arr = Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : [];
      setTitulares(arr);
    }).catch(() => {});
  }, []);

  // Faturas pagas aqui que NÃO tiraram o comprometido do cartão no TechMiles.
  // Enquanto isso não sai de lá, o limite continua aparecendo como usado e
  // ninguém compra com um limite que na verdade já está livre — o pagamento
  // parece feito de um lado e inexistente do outro.
  const carregarPendencias = useCallback(() => {
    fetch(`${API}/integracao/baixas-pendentes`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setBaixas(d?.resumo ? d : null))
      .catch(() => {});
  }, []);
  useEffect(() => { carregarPendencias(); }, [carregarPendencias]);

  const reenviarBaixas = async () => {
    if (!baixas?.problemas?.length) return;
    setReenviando(true);
    try {
      const rs = [];
      for (const p of baixas.problemas) {
        const r = await fetch(`${API}/integracao/reenviar-baixa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ pagamento_id: p.pagamento_id, apenas_sem_aviso: false }),
        }).then(x => x.json()).catch(() => null);
        rs.push(...(r?.resultados || []));
      }
      const liberou = rs.filter(x => x.liberou).length;
      const semEfeito = rs.filter(x => x.ok && !x.liberou);
      const falhou = rs.filter(x => !x.ok);
      let msg = `${liberou} de ${rs.length} liberaram o comprometido no TechMiles.`;
      if (semEfeito.length) {
        msg += `\n\n${semEfeito.length} foram aceitos mas não liberaram nada (o comprometido continua preso lá):\n`
             + semEfeito.slice(0, 5).map(x => `• ${x.cartao} ${x.mes_referencia}`).join('\n');
      }
      if (falhou.length) {
        msg += `\n\n${falhou.length} falharam:\n`
             + falhou.slice(0, 5).map(x => `• ${x.cartao} ${x.mes_referencia}: ${x.motivo}`).join('\n');
      }
      alert(msg);
      carregarPendencias();
    } finally { setReenviando(false); }
  };

  const openPagar = (item) => {
    setPagarModal(item);
    const saldo = (item.saldo_restante != null ? item.saldo_restante : item.valor) || 0;
    setPagarForm({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_pago: String(saldo),
      forma_pagamento: '',
      observacoes: '',
    });
  };

  const salvarPagamento = async () => {
    if (!pagarModal) return;
    const valor = parseFloat(pagarForm.valor_pago);
    if (!(valor > 0)) return alert('Valor do pagamento deve ser maior que zero');
    setSaving(true);
    try {
      // POST /:id/pagamentos suporta múltiplos pagamentos parciais (acumula)
      const r = await fetch(`${API}/contas-a-pagar/${pagarModal.id}/pagamentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          data_pagamento: pagarForm.data_pagamento,
          valor_pago: valor,
          forma_pagamento: pagarForm.forma_pagamento || null,
          observacoes: pagarForm.observacoes || null,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      const result = await r.json();
      setPagarModal(null);
      load();
      // A baixa no TechMiles roda em background e pode não liberar nada. Recarrega
      // a checagem pra que uma falha apareça logo, e não meses depois.
      setTimeout(carregarPendencias, 2500);
      // Feedback opcional sobre saldo restante
      if (result.saldoRestante > 0.01) {
        // não bloquear; load() já refletirá saldo
      }
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const recarregarPagamentos = async (capId, novoStatus) => {
    if (pagarModal?.id !== capId && detalheModal?.id !== capId) return;
    const refreshed = await fetch(`${API}/contas-a-pagar/${capId}/pagamentos`, { headers: authHeaders() }).then(r => r.json());
    const atualizar = prev => prev?.id === capId ? {
      ...prev,
      status: novoStatus || prev.status,
      pagamentos: refreshed.pagamentos || [],
      total_pago_acumulado: refreshed.total_pago || 0,
      saldo_restante: refreshed.saldo || 0,
    } : prev;
    setPagarModal(atualizar);
    setDetalheModal(atualizar);
  };

  const salvarEdicaoPagamento = async (capId) => {
    try {
      const r = await fetch(`${API}/contas-a-pagar/${capId}/pagamentos/${editandoPag.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editandoPag),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      setEditandoPag(null);
      await recarregarPagamentos(capId);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const estornarPagamento = async (capId, pagamentoId) => {
    if (!window.confirm('Desfazer este pagamento? Os lançamentos no fluxo de caixa também serão removidos.\n\nSe a fatura estava quitada, ela volta a constar como aberta aqui, mas a baixa já enviada ao TechMiles NÃO é desfeita — ajuste lá.')) return;
    setEstornandoId(pagamentoId);
    try {
      const r = await fetch(`${API}/contas-a-pagar/${capId}/pagamentos/${pagamentoId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      const { status: novoStatus } = await r.json();
      await recarregarPagamentos(capId, novoStatus);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setEstornandoId(null);
  };

  const cancelarItem = async (id) => {
    if (!window.confirm('Cancelar este item?')) return;
    await fetch(`${API}/contas-a-pagar/${id}/cancelar`, {
      method: 'PATCH', headers: authHeaders(),
    });
    load();
  };

  const salvarNovo = async () => {
    if (!novoForm.valor || !novoForm.data_vencimento) return alert('Valor e vencimento são obrigatórios');
    setSaving(true);
    try {
      const r = await fetch(`${API}/contas-a-pagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(novoForm),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      setNovoModal(false);
      setNovoForm({ tipo: 'FATURA', cartao_id: '', titular_id: '', nome_cartao: '', descricao: '', valor: '', data_vencimento: '', banco: '', observacoes: '' });
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const hoje = new Date().toISOString().split('T')[0];
  const em7 = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const statusVisual = (item) => {
    if (item.status === 'PAGO') return { label: 'Pago', color: colors.success };
    if (item.status === 'PARCIAL') {
      const pago = item.total_pago_acumulado || 0;
      const total = item.valor || 0;
      const pct = total > 0 ? Math.round((pago / total) * 100) : 0;
      return { label: `Parcial ${pct}%`, color: STATUS_CORES.PARCIAL };
    }
    if (item.status === 'CANCELADO') return { label: 'Cancelado', color: colors.textDim };
    if (item.status === 'ATRASADO' || item.data_vencimento < hoje) return { label: 'Atrasado', color: colors.danger };
    if (item.data_vencimento <= em7) return { label: 'Vence em breve', color: colors.warning };
    return { label: 'Pendente', color: colors.textMuted };
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Contas a Pagar"
        subtitle={`${data.length} item(s) encontrado(s)`}
        onAdd={readOnly ? undefined : () => setNovoModal(true)}
        addLabel={readOnly ? undefined : '+ Novo'}
      />

      {/* KPIs principais — Total a Pagar / Total Pago */}
      {(() => {
        const aPagarQtd = (kpis.pendente?.qtd || 0) + (kpis.atrasado?.qtd || 0);
        const aPagarTotal = (parseFloat(kpis.pendente?.total) || 0) + (parseFloat(kpis.atrasado?.total) || 0);
        const pagoQtd = kpis.pago_mes?.qtd || 0;
        const pagoTotal = parseFloat(kpis.pago_mes?.total) || 0;
        return (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{
              flex: '1 1 280px', minWidth: 240, padding: 18,
              background: `linear-gradient(135deg, ${colors.danger}11, ${colors.danger}05)`,
              border: `1px solid ${colors.danger}44`, borderRadius: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total a Pagar {filtros.mes ? `(${filtros.mes})` : ''}
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: colors.danger, marginTop: 4 }}>
                  {fmt(aPagarTotal)}
                </div>
                <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>
                  {aPagarQtd} item(s) — pendentes + atrasados (exceto cartões em negociação)
                </div>
              </div>
              <AlertTriangle size={32} color={colors.danger} style={{ opacity: 0.4 }} />
            </div>

            <div style={{
              flex: '1 1 280px', minWidth: 240, padding: 18,
              background: `linear-gradient(135deg, ${colors.success}11, ${colors.success}05)`,
              border: `1px solid ${colors.success}44`, borderRadius: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total Pago (mês)
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: colors.success, marginTop: 4 }}>
                  {fmt(pagoTotal)}
                </div>
                <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>
                  {pagoQtd} pagamento(s) registrado(s)
                </div>
              </div>
              <CheckCircle size={32} color={colors.success} style={{ opacity: 0.4 }} />
            </div>
          </div>
        );
      })()}

      {/* Alerta: paguei aqui, mas o comprometido não saiu do TechMiles.
          Sem isso o erro fica invisível — a linha mostra PAGO e o cartão
          continua travado do outro lado. */}
      {baixas?.resumo?.comprometido_preso > 0 && (
        <div style={{
          padding: 16, marginBottom: 16, borderRadius: 12,
          background: `linear-gradient(135deg, ${colors.warning}18, ${colors.warning}06)`,
          border: `1px solid ${colors.warning}55`,
          display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <AlertTriangle size={26} color={colors.warning} style={{ flexShrink: 0 }} />
          <div style={{ flex: '1 1 340px', minWidth: 280 }}>
            <div style={{ fontWeight: 700, color: colors.warning, fontSize: 14 }}>
              {baixas.resumo.comprometido_preso} pagamento(s) não liberaram o limite no TechMiles
            </div>
            <div style={{ fontSize: 12.5, color: colors.textDim, marginTop: 3, lineHeight: 1.5 }}>
              {fmt(baixas.resumo.valor_pago_sem_liberar)} já pagos aqui, mas o cartão continua comprometido lá —
              o limite aparece como usado sem estar.
              {baixas.resumo.nunca_avisou > 0 && ` ${baixas.resumo.nunca_avisou} nunca chegaram a avisar.`}
              {baixas.resumo.sem_efeito > 0 && ` ${baixas.resumo.sem_efeito} avisaram e o TechMiles não baixou nada.`}
            </div>
            <div style={{ fontSize: 11.5, color: colors.textMuted, marginTop: 6 }}>
              {baixas.problemas.slice(0, 4).map(p => `${p.cartao} (${p.mes_referencia})`).join(' · ')}
              {baixas.problemas.length > 4 ? ` · +${baixas.problemas.length - 4}` : ''}
            </div>
          </div>
          {!readOnly && (
            <button
              onClick={reenviarBaixas}
              disabled={reenviando}
              style={{
                padding: '9px 16px', borderRadius: 8, cursor: reenviando ? 'wait' : 'pointer',
                background: colors.warning, color: '#111', border: 'none',
                fontWeight: 700, fontSize: 13, opacity: reenviando ? 0.6 : 1,
              }}
            >
              {reenviando ? 'Reenviando...' : 'Reenviar ao TechMiles'}
            </button>
          )}
        </div>
      )}

      {/* KPIs detalhados */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Pendente" icon={Clock}
          value={kpis.pendente?.qtd || 0}
          subValue={fmt(kpis.pendente?.total)}
          color={colors.warning} />
        <StatCard label="Atrasado" icon={AlertTriangle}
          value={kpis.atrasado?.qtd || 0}
          subValue={fmt(kpis.atrasado?.total)}
          color={colors.danger} />
        <StatCard label="A vencer 30 dias" icon={Clock}
          value={kpis.a_vencer_30?.qtd || 0}
          subValue={fmt(kpis.a_vencer_30?.total)}
          color={colors.primary} />
        <StatCard label="Pago no mês" icon={CheckCircle}
          value={kpis.pago_mes?.qtd || 0}
          subValue={fmt(kpis.pago_mes?.total)}
          color={colors.success} />
      </div>

      {/* Filtros */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão, titular, descrição...">
        {/* Investidor: sem seletor de titular (travado no dele). Gestor: livre. */}
        {!titularRestrito && (
          <Select value={filtros.titular_id} onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value }))} style={{ width: 'auto', minWidth: 160 }}>
            <option value="">Todos Titulares</option>
            {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </Select>
        )}
        <Select value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))} style={{ width: 'auto', minWidth: 180 }}>
          <option value="">Todos os Tipos</option>
          <option value="FATURA">Fatura</option>
          <option value="FATURA_CRISE">Fatura Crise</option>
          <option value="ACORDO_TITULAR">Acordo c/ Titular</option>
          <option value="ACORDO_BANCO">Acordo c/ Banco</option>
        </Select>
        <Select value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todos os Status</option>
          <option value="PENDENTE">Pendente</option>
          <option value="ATRASADO">Atrasado</option>
          <option value="PAGO">Pago</option>
          <option value="CANCELADO">Cancelado</option>
        </Select>
        <Input
          type="month" value={filtros.mes}
          onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}
          style={{ width: 160 }}
        />
        <button
          onClick={() => setFiltros(f => ({ ...f, mes: '' }))}
          style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}
        >
          Todos meses
        </button>
      </SearchBar>

      {data.length === 0 ? (
        <EmptyState message="Nenhuma conta a pagar encontrada" icon={DollarSign} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Vencimento</Th>
            <Th>Titular</Th>
            <Th>Cartão / Banco</Th>
            <Th>Descrição</Th>
            <Th>Tipo</Th>
            <Th>Parcela</Th>
            <Th>Fatura Total</Th>
            <Th>Conciliado</Th>
            <Th>Conferência</Th>
            <Th>Status</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {data.map(item => {
              const sv = statusVisual(item);
              const isAtivo = item.status === 'PENDENTE' || item.status === 'ATRASADO' || item.status === 'PARCIAL';
              return (
                <TrHover key={item.id}
                onClick={() => abrirDetalhe(item)}
                style={{
                  cursor: 'pointer',
                  opacity: item.status === 'CANCELADO' ? 0.5 : 1,
                  background: item.status === 'ATRASADO' ? `${colors.danger}08` :
                              item.data_vencimento <= em7 && isAtivo ? `${colors.warning}08` : undefined,
                }}>
                  <Td style={{ whiteSpace: 'nowrap', fontWeight: 600,
                    color: item.status === 'ATRASADO' ? colors.danger :
                           item.data_vencimento <= em7 && isAtivo ? colors.warning : colors.text }}>
                    {item.data_vencimento}
                  </Td>
                  <Td style={{ fontSize: 12 }}>{item.titular_nome || '-'}</Td>
                  <Td style={{ fontSize: 12 }}>
                    <div>{item.nome_cartao || '-'}</div>
                    {(item.banco_nome || item.banco) && (
                      <div style={{ color: colors.textDim, fontSize: 11 }}>{item.banco_nome || item.banco}</div>
                    )}
                  </Td>
                  <Td style={{ fontSize: 12, maxWidth: 220 }}>{item.descricao || '-'}</Td>
                  <Td>
                    <span style={{
                      background: (TIPO_CORES[item.tipo] || colors.primary) + '22',
                      color: TIPO_CORES[item.tipo] || colors.primary,
                      border: `1px solid ${(TIPO_CORES[item.tipo] || colors.primary)}44`,
                      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    }}>
                      {TIPO_LABELS[item.tipo] || item.tipo}
                    </span>
                  </Td>
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>
                    {item.parcela_num ? `${item.parcela_num}/${item.parcela_total}` : '-'}
                  </Td>
                  {/* Fatura Total — só para FATURA / FATURA_CRISE */}
                  <Td style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
                    {(item.tipo === 'FATURA' || item.tipo === 'FATURA_CRISE') && item.valor_fatura_total
                      ? fmt(item.valor_fatura_total)
                      : <span style={{ color: colors.textDim }}>—</span>}
                  </Td>
                  {/* Valor Conciliado */}
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>
                    {(item.tipo === 'FATURA' || item.tipo === 'FATURA_CRISE') && item.valor_conciliado != null
                      ? fmt(item.valor_conciliado)
                      : <span style={{ color: colors.textDim }}>—</span>}
                  </Td>
                  {/* Conferência */}
                  <Td>
                    {(item.tipo === 'FATURA' || item.tipo === 'FATURA_CRISE') && item.total_lancamentos > 0 ? (
                      item.conferencia_ok
                        ? <span style={{ fontSize: 11, background: `${colors.success}22`, color: colors.success, border: `1px solid ${colors.success}44`, borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>✓ OK</span>
                        : <span style={{ fontSize: 11, background: `${colors.warning}22`, color: colors.warning, border: `1px solid ${colors.warning}44`, borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>Pendente</span>
                    ) : <span style={{ color: colors.textDim, fontSize: 12 }}>—</span>}
                  </Td>
                  <Td style={{ fontWeight: 700, color: isAtivo ? colors.danger : colors.textMuted }}>
                    {/* Chamado #56: o "a pagar" é a parte da EMPRESA (total − pessoal).
                        A parte pessoal aparece abaixo, marcada como do titular. */}
                    {fmt(item.valor_empresa != null ? item.valor_empresa : item.valor)}
                    {item.valor_pessoal > 0.01 && (
                      <div style={{ fontSize: 11, color: colors.textDim, fontWeight: 500 }}
                        title="Parte pessoal — responsabilidade do titular. Não entra no valor a pagar da empresa.">
                        pessoal (titular): {fmt(item.valor_pessoal)}
                      </div>
                    )}
                    {item.sem_divida_empresa && (
                      <div style={{ fontSize: 11, color: colors.textDim, fontStyle: 'italic' }}>sem dívida da empresa</div>
                    )}
                    {item.status === 'PAGO' && item.total_pago_acumulado > 0 && (
                      <div style={{ fontSize: 11, color: colors.success }}>pago: {fmt(item.total_pago_acumulado)}</div>
                    )}
                    {item.status === 'PARCIAL' && (
                      <>
                        <div style={{ fontSize: 11, color: STATUS_CORES.PARCIAL }}>
                          pago: {fmt(item.total_pago_acumulado || 0)}
                        </div>
                        <div style={{ fontSize: 11, color: colors.warning }}>
                          saldo empresa: {fmt(item.saldo_restante || 0)}
                        </div>
                      </>
                    )}
                  </Td>
                  <Td><Badge text={sv.label} color={sv.color} /></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                      {/* PDF */}
                      {(item.tipo === 'FATURA' || item.tipo === 'FATURA_CRISE') && item.cartao_id && item.mes_referencia && (
                        <button onClick={(e) => abrirPdf(item, e)} title="Abrir PDF da fatura"
                          style={{ background: `${colors.primary}22`, color: colors.primary, border: `1px solid ${colors.primary}44`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FileText size={12} /> PDF
                        </button>
                      )}
                      {/* Conciliar — quando fatura NÃO conciliada */}
                      {!readOnly && isAtivo && (item.tipo === 'FATURA' || item.tipo === 'FATURA_CRISE') && item.cartao_id && !podeoPagar(item) && (
                        <button onClick={(e) => irConciliar(item, e)} title="Ir para conciliação deste cartão"
                          style={{ background: `${colors.warning}22`, color: colors.warning, border: `1px solid ${colors.warning}44`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Conciliar
                        </button>
                      )}
                      {/* Pagar — liberado só se conciliado (ou não é fatura) */}
                      {!readOnly && isAtivo && (
                        <button onClick={() => openPagar(item)}
                          disabled={!podeoPagar(item)}
                          title={!podeoPagar(item) ? 'Concilie a fatura antes de pagar' : undefined}
                          style={{ background: podeoPagar(item) ? colors.success : colors.border,
                            color: podeoPagar(item) ? '#fff' : colors.textDim,
                            border: 'none', borderRadius: 4, padding: '4px 10px',
                            cursor: podeoPagar(item) ? 'pointer' : 'not-allowed',
                            fontSize: 11, fontWeight: 600, opacity: podeoPagar(item) ? 1 : 0.6 }}>
                          Pagar
                        </button>
                      )}
                      {/* Quitada perde o Pagar, e com ele o histórico: o acesso fica aqui (#72) */}
                      {!isAtivo && item.pagamentos?.length > 0 && (
                        <button onClick={() => abrirDetalhe(item)} title="Ver, corrigir ou desfazer os pagamentos desta fatura"
                          style={{ background: 'transparent', color: colors.success, border: `1px solid ${colors.success}44`,
                            borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Pagamentos ({item.pagamentos.length})
                        </button>
                      )}
                      {!readOnly && isAtivo && (
                        <button onClick={() => cancelarItem(item.id)}
                          style={{ background: 'transparent', color: colors.textDim, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}
                          title="Cancelar">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </Td>
                </TrHover>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Modal: Detalhe da Conta (lançamentos + PDF) */}
      <Modal isOpen={!!detalheModal} onClose={() => { setDetalheModal(null); setLancamentos([]); }}
        title={detalheModal ? `${detalheModal.nome_cartao || detalheModal.descricao || 'Detalhe'} — ${detalheModal.mes_referencia || ''}` : ''}
        width={820}>
        {detalheModal && (
          <>
            {/* Cabeçalho resumo */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, padding: 12,
              background: colors.bg, borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13 }}>
              <div><strong>Tipo:</strong> <span style={{ color: TIPO_CORES[detalheModal.tipo] }}>{TIPO_LABELS[detalheModal.tipo] || detalheModal.tipo}</span></div>
              <div><strong>Vencimento:</strong> {detalheModal.data_vencimento}</div>
              <div><strong>Valor:</strong> <span style={{ color: colors.danger, fontWeight: 700 }}>{fmt(detalheModal.valor)}</span></div>
              {detalheModal.valor_fatura_total && <div><strong>Fatura total:</strong> {fmt(detalheModal.valor_fatura_total)}</div>}
              {detalheModal.valor_conciliado != null && <div><strong>Conciliado:</strong> {fmt(detalheModal.valor_conciliado)}</div>}
              <div><strong>Status:</strong> <span style={{ color: STATUS_CORES[detalheModal.status] || colors.textMuted }}>{detalheModal.status}</span></div>
              {/* Botão abrir PDF */}
              {detalheModal.cartao_id && detalheModal.mes_referencia && (
                <button onClick={(e) => abrirPdf(detalheModal, e)}
                  style={{ marginLeft: 'auto', background: `${colors.primary}22`, color: colors.primary,
                    border: `1px solid ${colors.primary}44`, borderRadius: 6, padding: '4px 12px',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={14} /> Abrir PDF
                </button>
              )}
            </div>

            {(detalheModal.pagamentos?.length > 0 || detalheModal.total_pago_acumulado > 0) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Pagamentos ({detalheModal.pagamentos?.length || 0})</span>
                  <span>Total pago: <span style={{ color: colors.success }}>{fmt(detalheModal.total_pago_acumulado || 0)}</span></span>
                </div>
                {detalheModal.pagamentos?.length > 0 && (
                  <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6 }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: colors.bg, borderBottom: `1px solid ${colors.border}` }}>
                          {['Data', 'Valor', 'Tipo', 'Forma', ''].map((h, i) => (
                            <th key={i} style={{ padding: '6px 10px', textAlign: h === 'Valor' ? 'right' : 'left', color: colors.textMuted, fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detalheModal.pagamentos.map(p => (
                          <tr key={p.id} style={{ borderBottom: `1px solid ${colors.border}33` }}>
                            <td style={{ padding: '5px 10px' }}>
                              {editandoPag?.id === p.id
                                ? <input type="date" aria-label="Data do pagamento" value={editandoPag.data_pagamento}
                                    onChange={e => setEditandoPag({ ...editandoPag, data_pagamento: e.target.value })} />
                                : p.data_pagamento}
                            </td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: colors.success }}>{fmt(p.valor_pago)}</td>
                            <td style={{ padding: '5px 10px', color: colors.textDim }}
                              title={p.tipo_pagamento === 'ANTECIPADO' ? 'Pago antes do fechamento: o banco já descontou da fatura, não entra no "Já pago". Estorno na tela de Cartões.' : undefined}>
                              {p.tipo_pagamento === 'ANTECIPADO' ? 'ANTECIPADO (fora da quitação)' : (p.tipo_pagamento || '-')}
                            </td>
                            <td style={{ padding: '5px 10px', color: colors.textDim }}>
                              {editandoPag?.id === p.id
                                ? <input aria-label="Forma do pagamento" value={editandoPag.forma_pagamento || ''} style={{ width: 90 }}
                                    onChange={e => setEditandoPag({ ...editandoPag, forma_pagamento: e.target.value })} />
                                : (p.forma_pagamento || '-')}
                            </td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {!readOnly && p.tipo_pagamento !== 'ANTECIPADO' && editandoPag?.id === p.id && (
                                <>
                                  <button onClick={() => salvarEdicaoPagamento(detalheModal.id)}
                                    style={{ background: colors.success, color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, marginRight: 4 }}>
                                    Salvar
                                  </button>
                                  <button onClick={() => setEditandoPag(null)}
                                    style={{ background: 'transparent', color: colors.textDim, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>
                                    Cancelar
                                  </button>
                                </>
                              )}
                              {!readOnly && p.tipo_pagamento !== 'ANTECIPADO' && editandoPag?.id !== p.id && (
                                <button onClick={() => setEditandoPag({ id: p.id, data_pagamento: p.data_pagamento, forma_pagamento: p.forma_pagamento, observacoes: p.observacoes })}
                                  style={{ background: 'transparent', color: colors.textMuted, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, marginRight: 4 }}>
                                  Editar
                                </button>
                              )}
                              {!readOnly && p.tipo_pagamento !== 'ANTECIPADO' && editandoPag?.id !== p.id && (
                                <button onClick={() => estornarPagamento(detalheModal.id, p.id)}
                                  disabled={estornandoId === p.id}
                                  style={{ background: 'transparent', color: colors.danger, border: `1px solid ${colors.danger}44`,
                                    borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11,
                                    opacity: estornandoId === p.id ? 0.4 : 1 }}>
                                  Desfazer
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Lançamentos */}
            {loadingLanc ? (
              <div style={{ textAlign: 'center', padding: 32, color: colors.textDim }}>Carregando lançamentos...</div>
            ) : lancamentos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: colors.textDim }}>
                {detalheModal.cartao_id ? 'Nenhum lançamento importado para este mês.' : 'Sem cartão vinculado — nenhum lançamento disponível.'}
              </div>
            ) : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      {['Data','Descrição','Categoria','Valor','Origem','Parcela'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lancamentos.map((l, i) => (
                      <tr key={l.id || i} style={{ borderBottom: `1px solid ${colors.border}22`,
                        background: i % 2 === 0 ? 'transparent' : `${colors.card}44` }}>
                        <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: colors.textMuted }}>{l.data_compra || l.data || '—'}</td>
                        <td style={{ padding: '5px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.descricao}>{l.descricao || '—'}</td>
                        <td style={{ padding: '5px 10px' }}>
                          {l.categoria ? (
                            <span style={{ background: `${colors.primary}22`, color: colors.primary,
                              borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>{l.categoria}</span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', fontWeight: 600,
                          color: (l.valor || 0) < 0 ? colors.success : colors.text }}>{fmt(l.valor)}</td>
                        <td style={{ padding: '5px 10px' }}>
                          <span style={{ fontSize: 10, color: colors.textDim,
                            background: `${colors.border}`, borderRadius: 3, padding: '1px 5px' }}>{l.origem || '—'}</span>
                        </td>
                        <td style={{ padding: '5px 10px', color: colors.textDim }}>
                          {l.parcela_atual && l.total_parcelas ? `${l.parcela_atual}/${l.total_parcelas}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${colors.border}` }}>
                      <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, color: colors.textMuted, fontSize: 12 }}>
                        Total ({lancamentos.length} lançamentos)
                      </td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: colors.danger, fontSize: 13 }}>
                        {fmt(lancamentos.reduce((s, l) => s + (l.valor || 0), 0))}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
              {!readOnly && detalheModal && (detalheModal.status === 'PENDENTE' || detalheModal.status === 'ATRASADO' || detalheModal.status === 'PARCIAL') && !podeoPagar(detalheModal) && (
                <Button color={colors.warning} onClick={(e) => { setDetalheModal(null); irConciliar(detalheModal, e); }}>
                  Conciliar Primeiro
                </Button>
              )}
              {!readOnly && detalheModal && (detalheModal.status === 'PENDENTE' || detalheModal.status === 'ATRASADO' || detalheModal.status === 'PARCIAL') && podeoPagar(detalheModal) && (
                <Button color={colors.success} onClick={() => { setDetalheModal(null); openPagar(detalheModal); }}>
                  Registrar Pagamento
                </Button>
              )}
              <Button variant="secondary" onClick={() => { setDetalheModal(null); setLancamentos([]); }}>Fechar</Button>
            </div>
          </>
        )}
      </Modal>

      {/* Modal: Registrar Pagamento (parcial ou total) */}
      <Modal isOpen={!!pagarModal} onClose={() => setPagarModal(null)}
        title={`Registrar Pagamento — ${pagarModal?.nome_cartao || pagarModal?.descricao || ''}`} width={560}>
        {pagarModal && (() => {
          const totalPago = pagarModal.total_pago_acumulado || 0;
          // Chamado #56: saldo = parte da EMPRESA; saldoTotal inclui o pessoal.
          const saldo = pagarModal.saldo_restante != null ? pagarModal.saldo_restante : (pagarModal.valor - totalPago);
          const valorPessoal = pagarModal.valor_pessoal || 0;
          const saldoTotal = pagarModal.saldo_total != null ? pagarModal.saldo_total : (pagarModal.valor - totalPago);
          const historico = pagarModal.pagamentos || [];
          const valorAPagar = parseFloat(pagarForm.valor_pago) || 0;
          const futuroSaldo = saldo - valorAPagar;
          return (
          <>
            <div style={{
              background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 12,
              border: `1px solid ${colors.border}`, fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap',
            }}>
              <div><strong>Tipo:</strong> {TIPO_LABELS[pagarModal.tipo]}</div>
              <div><strong>Vencimento:</strong> {pagarModal.data_vencimento}</div>
              <div><strong>Fatura total:</strong> <span style={{ fontWeight: 700 }}>{fmt(pagarModal.valor)}</span></div>
              {/* Chamado #56: quebra empresa vs pessoal (titular) */}
              {valorPessoal > 0.01 && <>
                <div><strong>Empresa:</strong> <span style={{ color: colors.danger, fontWeight: 700 }}>{fmt(pagarModal.valor_empresa)}</span></div>
                <div title="Responsabilidade do titular — não é dívida da empresa"><strong>Pessoal (titular):</strong> <span style={{ color: colors.textMuted, fontWeight: 700 }}>{fmt(valorPessoal)}</span></div>
              </>}
              {pagarModal.parcela_num && <div><strong>Parcela:</strong> {pagarModal.parcela_num}/{pagarModal.parcela_total}</div>}
            </div>

            {/* Saldo */}
            <div style={{
              background: totalPago > 0 ? `${STATUS_CORES.PARCIAL}11` : `${colors.warning}11`,
              borderRadius: 8, padding: 12, marginBottom: 12,
              border: `1px solid ${totalPago > 0 ? `${STATUS_CORES.PARCIAL}44` : `${colors.warning}44`}`,
              display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, fontSize: 13,
            }}>
              <div><strong>Já pago:</strong> <span style={{ color: STATUS_CORES.PARCIAL, fontWeight: 700 }}>{fmt(totalPago)}</span></div>
              <div><strong>Saldo da empresa:</strong> <span style={{ color: colors.warning, fontWeight: 700 }}>{fmt(Math.max(0, saldo))}</span></div>
              {valorPessoal > 0.01 && (
                <div style={{ color: colors.textDim }}>Pagar a empresa já quita a fatura — o pessoal ({fmt(valorPessoal)}) é do titular.</div>
              )}
            </div>

            {/* Histórico de pagamentos */}
            {historico.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, marginBottom: 6 }}>
                  Histórico ({historico.length} pagamento{historico.length > 1 ? 's' : ''})
                </div>
                <div style={{ maxHeight: 140, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 6 }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: colors.bg, borderBottom: `1px solid ${colors.border}` }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Data</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', color: colors.textMuted, fontWeight: 600 }}>Valor</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Tipo</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Forma</th>
                        <th style={{ width: 30 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {historico.map(p => (
                        <tr key={p.id} style={{ borderBottom: `1px solid ${colors.border}33` }}>
                          <td style={{ padding: '5px 10px' }}>{p.data_pagamento}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: colors.success }}>{fmt(p.valor_pago)}</td>
                          <td style={{ padding: '5px 10px', color: colors.textDim }}
                            title={p.tipo_pagamento === 'ANTECIPADO' ? 'Pago antes do fechamento: o banco já descontou da fatura, não entra no "Já pago". Estorno na tela de Cartões.' : undefined}>
                            {p.tipo_pagamento === 'ANTECIPADO' ? 'ANTECIPADO (fora da quitação)' : (p.tipo_pagamento || '-')}
                          </td>
                          <td style={{ padding: '5px 10px', color: colors.textDim }}>{p.forma_pagamento || '-'}</td>
                          <td style={{ padding: '5px 6px' }}>
                            {p.tipo_pagamento !== 'ANTECIPADO' && <button onClick={() => estornarPagamento(pagarModal.id, p.id)}
                              disabled={estornandoId === p.id}
                              title="Estornar este pagamento"
                              style={{ background: 'transparent', border: 'none', color: colors.danger,
                                cursor: 'pointer', fontSize: 11, padding: 2,
                                opacity: estornandoId === p.id ? 0.4 : 1 }}>
                              <X size={14} />
                            </button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {saldo > 0.01 ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <FormField label="Data do Pagamento">
                    <Input type="date" value={pagarForm.data_pagamento}
                      onChange={e => setPagarForm(f => ({ ...f, data_pagamento: e.target.value }))} />
                  </FormField>
                  <FormField label={`Valor a pagar (saldo: ${fmt(saldo)})`}>
                    <Input type="number" step="0.01" value={pagarForm.valor_pago}
                      onChange={e => setPagarForm(f => ({ ...f, valor_pago: e.target.value }))} />
                    {/* Chamado #56: presets Empresa (quita a fatura) e Total (inclui pessoal). */}
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => setPagarForm(f => ({ ...f, valor_pago: String(saldo) }))}
                        style={{ fontSize: 10, padding: '2px 8px', background: `${colors.success}18`,
                          border: `1px solid ${colors.success}55`, borderRadius: 4, cursor: 'pointer', color: colors.success, fontWeight: 600 }}>
                        Empresa ({fmt(saldo)})
                      </button>
                      {valorPessoal > 0.01 && (
                        <button type="button" onClick={() => setPagarForm(f => ({ ...f, valor_pago: String(saldoTotal) }))}
                          style={{ fontSize: 10, padding: '2px 8px', background: 'transparent',
                            border: `1px solid ${colors.border}`, borderRadius: 4, cursor: 'pointer', color: colors.textMuted }}>
                          Total, com pessoal ({fmt(saldoTotal)})
                        </button>
                      )}
                      <button type="button" onClick={() => setPagarForm(f => ({ ...f, valor_pago: String(Math.round(saldo / 2 * 100) / 100) }))}
                        style={{ fontSize: 10, padding: '2px 8px', background: 'transparent',
                          border: `1px solid ${colors.border}`, borderRadius: 4, cursor: 'pointer', color: colors.textMuted }}>
                        Metade
                      </button>
                    </div>
                  </FormField>
                </div>
                <FormField label="Forma de pagamento (opcional)">
                  <Input type="text" placeholder="PIX, transferência, boleto..."
                    value={pagarForm.forma_pagamento}
                    onChange={e => setPagarForm(f => ({ ...f, forma_pagamento: e.target.value }))} />
                </FormField>
                <FormField label="Observações (opcional)">
                  <TextArea value={pagarForm.observacoes}
                    onChange={e => setPagarForm(f => ({ ...f, observacoes: e.target.value }))}
                    rows={2} />
                </FormField>

                {valorAPagar > 0 && (
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>
                    Após este pagamento → saldo restante: <strong style={{
                      color: futuroSaldo <= 0.01 ? colors.success : colors.warning,
                    }}>{fmt(Math.max(0, futuroSaldo))}</strong>
                    {futuroSaldo <= 0.01 && ' ✓ Fatura quitada'}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: 16, background: `${colors.success}11`, border: `1px solid ${colors.success}44`,
                borderRadius: 8, color: colors.success, textAlign: 'center', fontWeight: 600 }}>
                ✓ Fatura totalmente quitada
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setPagarModal(null)}>Fechar</Button>
              {saldo > 0.01 && (
                <Button onClick={salvarPagamento} disabled={saving || !(valorAPagar > 0)}>
                  {saving ? 'Salvando...' : 'Confirmar Pagamento'}
                </Button>
              )}
            </div>
          </>
          );
        })()}
      </Modal>

      {/* Modal: Novo (manual) */}
      <Modal isOpen={novoModal} onClose={() => setNovoModal(false)} title="Nova Conta a Pagar" width={560}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Tipo">
            <Select value={novoForm.tipo} onChange={e => setNovoForm(f => ({ ...f, tipo: e.target.value }))}>
              <option value="FATURA">Fatura</option>
              <option value="ACORDO_TITULAR">Acordo c/ Titular</option>
              <option value="ACORDO_BANCO">Acordo c/ Banco</option>
            </Select>
          </FormField>
          <FormField label="Titular">
            <Select value={novoForm.titular_id} onChange={e => setNovoForm(f => ({ ...f, titular_id: e.target.value }))}>
              <option value="">Selecionar...</option>
              {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </Select>
          </FormField>
          <FormField label="Nome do Cartão">
            <Input value={novoForm.nome_cartao} onChange={e => setNovoForm(f => ({ ...f, nome_cartao: e.target.value }))} />
          </FormField>
          <FormField label="Banco">
            <Input value={novoForm.banco} onChange={e => setNovoForm(f => ({ ...f, banco: e.target.value }))} />
          </FormField>
          <FormField label="Valor *">
            <Input type="number" step="0.01" value={novoForm.valor} onChange={e => setNovoForm(f => ({ ...f, valor: e.target.value }))} />
          </FormField>
          <FormField label="Data de Vencimento *">
            <Input type="date" value={novoForm.data_vencimento} onChange={e => setNovoForm(f => ({ ...f, data_vencimento: e.target.value }))} />
          </FormField>
        </div>
        <FormField label="Descrição">
          <Input value={novoForm.descricao} onChange={e => setNovoForm(f => ({ ...f, descricao: e.target.value }))} />
        </FormField>
        <FormField label="Observações">
          <TextArea value={novoForm.observacoes} onChange={e => setNovoForm(f => ({ ...f, observacoes: e.target.value }))} rows={2} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setNovoModal(false)}>Cancelar</Button>
          <Button onClick={salvarNovo} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </Modal>
    </div>
  );
}
